'use client';

import { useState, useCallback } from 'react';
import { parseCertificateUrl, extractShortCode } from '@/lib/parseCertificateUrl';
import { fetchCertificateByContract, resolveShortCode } from '@/lib/api';
import {
  type ReasonCode,
  type VerificationResult,
  type CertificateData,
  computeVerdict,
} from '@/lib/verification-verdict';

type VerificationState = {
  status: 'idle' | 'loading' | 'done' | 'error';
  result: VerificationResult | null;
  error: string | null;
};

export function useVerification() {
  const [state, setState] = useState<VerificationState>({
    status: 'idle',
    result: null,
    error: null,
  });

  const buildResult = (
    reasons: ReasonCode[],
    certificate?: CertificateData,
    shortUrlStatus?: 'authorized' | 'not_found' | 'skipped',
    originalUrl?: string,
  ): VerificationResult => {
    return {
      verdict: computeVerdict(reasons),
      reasons,
      certificate,
      shortUrlStatus,
      originalUrl,
    };
  };

  const verifyByUrl = useCallback(async (rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) return;

    setState({ status: 'loading', result: null, error: null });
    const reasons: ReasonCode[] = [];

    try {
      // Step 1: Try parsing as a full verification URL first
      let parsed = parseCertificateUrl(trimmed);
      let shortUrlStatus: 'authorized' | 'not_found' | 'skipped' = 'skipped';
      let originalUrl: string | undefined;

      // Step 2: If not a direct URL, try resolving as a short code
      if (!parsed.contract) {
        const shortInfo = extractShortCode(trimmed);
        if (shortInfo) {
          try {
            const resolved = await resolveShortCode(shortInfo.shortCode);
            if (resolved.authorized && resolved.originalUrl) {
              reasons.push('SHORT_URL_AUTHORIZED');
              shortUrlStatus = 'authorized';
              originalUrl = resolved.originalUrl;
              parsed = parseCertificateUrl(resolved.originalUrl);
            } else {
              reasons.push('SHORT_URL_NOT_FOUND');
              shortUrlStatus = 'not_found';
            }
          } catch {
            reasons.push('SHORT_URL_NOT_FOUND');
            shortUrlStatus = 'not_found';
          }
        }
      }

      // Step 3: If we still don't have params, it's an unrecognised URL
      if (!parsed.contract || !parsed.tokenId || !parsed.networkId) {
        if (!reasons.includes('SHORT_URL_NOT_FOUND')) {
          reasons.push('URL_PARSE_FAILED');
        }
        setState({
          status: 'done',
          result: buildResult(reasons, undefined, shortUrlStatus, originalUrl),
          error: null,
        });
        return;
      }

      // Step 4: Fetch certificate
      const cert = await fetchCertificateByContract(
        parsed.contract,
        parseInt(parsed.tokenId, 10),
        parseInt(parsed.networkId, 10),
      );

      reasons.push('CERT_FOUND');

      // Step 5: Check status
      const now = new Date();
      const expiryDate = new Date(cert.expiry);
      let status: 'valid' | 'revoked' | 'expired' = 'valid';

      if (cert.status === 'revoked') {
        reasons.push('CERT_REVOKED');
        status = 'revoked';
      } else if (expiryDate < now) {
        reasons.push('CERT_EXPIRED');
        status = 'expired';
      }

      const certificate: CertificateData = {
        id: cert.id,
        name: cert.name,
        competency: cert.competency,
        issued: cert.issued,
        expiry: cert.expiry,
        status,
        contract: cert.contract ?? parsed.contract,
        tokenId: cert.tokenId ?? parseInt(parsed.tokenId, 10),
        networkId: cert.networkId ?? parseInt(parsed.networkId, 10),
        imageURI: cert.imageURI,
        explorerUrl: cert.explorerUrl,
        shortUrl: cert.shortUrl,
        tenant: cert.tenant,
      };

      setState({
        status: 'done',
        result: buildResult(reasons, certificate, shortUrlStatus, originalUrl),
        error: null,
      });
    } catch (err) {
      const isNotFound =
        err instanceof Error && err.message.includes('not found');

      if (isNotFound) {
        reasons.push('CERT_NOT_FOUND');
        setState({
          status: 'done',
          result: buildResult(reasons),
          error: null,
        });
      } else {
        setState({
          status: 'error',
          result: null,
          error: err instanceof Error ? err.message : 'Verification failed',
        });
      }
    }
  }, []);

  const verifyManual = useCallback(
    async (contract: string, tokenId: string, networkId: string) => {
      if (!contract || !tokenId || !networkId) return;

      setState({ status: 'loading', result: null, error: null });
      const reasons: ReasonCode[] = [];

      try {
        const cert = await fetchCertificateByContract(
          contract,
          parseInt(tokenId, 10),
          parseInt(networkId, 10),
        );

        reasons.push('CERT_FOUND');

        const now = new Date();
        const expiryDate = new Date(cert.expiry);
        let status: 'valid' | 'revoked' | 'expired' = 'valid';

        if (cert.status === 'revoked') {
          reasons.push('CERT_REVOKED');
          status = 'revoked';
        } else if (expiryDate < now) {
          reasons.push('CERT_EXPIRED');
          status = 'expired';
        }

        const certificate: CertificateData = {
          id: cert.id,
          name: cert.name,
          competency: cert.competency,
          issued: cert.issued,
          expiry: cert.expiry,
          status,
          contract: cert.contract ?? contract,
          tokenId: cert.tokenId ?? parseInt(tokenId, 10),
          networkId: cert.networkId ?? parseInt(networkId, 10),
          imageURI: cert.imageURI,
          explorerUrl: cert.explorerUrl,
          shortUrl: cert.shortUrl,
          tenant: cert.tenant,
        };

        setState({
          status: 'done',
          result: buildResult(reasons, certificate, 'skipped'),
          error: null,
        });
      } catch (err) {
        const isNotFound =
          err instanceof Error && err.message.includes('not found');

        if (isNotFound) {
          reasons.push('CERT_NOT_FOUND');
          setState({
            status: 'done',
            result: buildResult(reasons),
            error: null,
          });
        } else {
          setState({
            status: 'error',
            result: null,
            error: err instanceof Error ? err.message : 'Verification failed',
          });
        }
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState({ status: 'idle', result: null, error: null });
  }, []);

  return { ...state, verifyByUrl, verifyManual, reset };
}
